<template>
  <Teleport to="body">
    <div v-if="modelValue" class="meu-perfil-backdrop" :style="{ zIndex }" @click.self="cancelar">
      <div class="meu-perfil-modal shadow" role="dialog" aria-modal="true">
        <!-- Header -->
        <header class="meu-perfil-header">
          <h5 class="mb-0"><i class="bi bi-person-gear me-2"></i>Meu Perfil</h5>
          <button type="button" class="btn-close btn-close-white" @click="cancelar" aria-label="Fechar"></button>
        </header>

        <!-- Body -->
        <div class="meu-perfil-body">
          <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Carregando...</span>
            </div>
          </div>

          <template v-else-if="usuario">
            <!-- Nome de Guerra -->
            <div class="mb-3">
              <label class="form-label fw-bold">Nome de Guerra</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': nomeGuerraError }"
                v-model="form.nomeExibicao"
                placeholder="Como você quer ser chamado internamente"
                maxlength="100"
                @input="validarNomeGuerra"
              />
              <div v-if="nomeGuerraError" class="invalid-feedback">{{ nomeGuerraError }}</div>
              <div v-else class="form-text">Máximo 3 palavras, mínimo 2 caracteres cada. Editável por você.</div>
            </div>

            <!-- Nickname -->
            <div class="mb-3">
              <label class="form-label fw-bold">Nickname</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': apelidoError || (!isApelidoValido && form.apelido) }"
                v-model="form.apelido"
                placeholder="Ex: joao_silva_23"
                maxlength="50"
                @input="validarApelidoInput; verificarApelidoDebounced()"
              />
              <div v-if="apelidoError" class="invalid-feedback">{{ apelidoError }}</div>
              <div v-else-if="!isApelidoValido && form.apelido" class="text-warning small mt-1">
                <i class="bi bi-exclamation-triangle me-1"></i>Apenas letras, números e underscore. Sem espaços.
              </div>
              <div v-else-if="apelidoDisponivel === true" class="text-success small mt-1">
                <i class="bi bi-check-circle me-1"></i>Nickname disponível
              </div>
              <div v-else-if="apelidoDisponivel === false" class="text-danger small mt-1">
                <i class="bi bi-x-circle me-1"></i>Nickname já está em uso
              </div>
              <div class="form-text">Nickname único para @menções no feed de sugestões.</div>
            </div>

            <!-- Foto Interna -->
            <div class="mb-3">
              <label class="form-label fw-bold">Foto Interna</label>
              <div class="d-flex align-items-center gap-3">
                <div class="foto-preview">
                  <div v-if="fotoInternaPreview" class="preview-img-wrapper">
                    <img :src="fotoInternaPreview" alt="Foto Interna" class="preview-img" />
                  </div>
                  <div v-else class="preview-placeholder">
                    <i class="bi bi-person"></i>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <label class="btn btn-sm btn-outline-primary mb-1">
                    <i class="bi bi-upload me-1"></i>Escolher arquivo
                    <input type="file" accept="image/*" @change="onFotoInternaChange" class="d-none" />
                  </label>
                  <button v-if="fotoInternaPreview || usuario.urlFotoInterna" type="button" class="btn btn-sm btn-outline-danger ms-1 mb-1" @click="removerFotoInterna">
                    <i class="bi bi-trash me-1"></i>Remover
                  </button>
                </div>
              </div>
            </div>

            <!-- Checkbox: mesma foto (entre as duas fotos) -->
            <div class="mb-3 form-check">
              <input class="form-check-input" type="checkbox" id="mesmaFoto" v-model="usarMesmaFoto" @change="aplicarMesmaFoto" />
              <label class="form-check-label" for="mesmaFoto">
                Usar a mesma foto para os dois perfis
              </label>
            </div>

            <!-- Foto Pública -->
            <div class="mb-3">
              <label class="form-label fw-bold">Foto Pública</label>
              <div class="d-flex align-items-center gap-3">
                <div class="foto-preview">
                  <div v-if="fotoPublicaPreview" class="preview-img-wrapper">
                    <img :src="fotoPublicaPreview" alt="Foto Pública" class="preview-img" />
                  </div>
                  <div v-else class="preview-placeholder">
                    <i class="bi bi-globe"></i>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <label class="btn btn-sm btn-outline-primary mb-1">
                    <i class="bi bi-upload me-1"></i>Escolher arquivo
                    <input type="file" accept="image/*" @change="onFotoPublicaChange" class="d-none" />
                  </label>
                  <button v-if="fotoPublicaPreview || usuario.urlFotoPublica" type="button" class="btn btn-sm btn-outline-danger ms-1 mb-1" @click="removerFotoPublica">
                    <i class="bi bi-trash me-1"></i>Remover
                  </button>
                </div>
              </div>
            </div>

            <!-- Notificações -->
            <div class="mb-3 form-check form-switch">
              <input class="form-check-input" type="checkbox" id="aceiteNotificacoes" v-model="form.aceiteNotificacoes" />
              <label class="form-check-label" for="aceiteNotificacoes">
                Aceito receber notificações de interações
              </label>
            </div>

            <!-- Termo de Uso -->
            <div class="mb-3 termo-section">
              <label class="form-label fw-bold">Termo de Uso — Perfil Público</label>
              <div class="termo-status mb-2">
                <span v-if="usuario.aceiteTermoUso && usuario.dataAceiteTermoUso" class="text-success small">
                  <i class="bi bi-check-circle me-1"></i>Aceito em {{ formatarData(usuario.dataAceiteTermoUso) }}
                </span>
                <span v-else class="text-danger small">
                  <i class="bi bi-x-circle me-1"></i>Não aceito
                </span>
              </div>
              <div class="d-flex gap-2">
                <button type="button" class="btn btn-outline-info btn-sm" @click="showTermoModal = true">
                  <i class="bi bi-file-text me-1"></i>Ler Termo
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="usuario.aceiteTermoUso ? 'btn-outline-warning' : 'btn-success'"
                  @click="toggleAceiteTermo"
                >
                  <i class="bi" :class="usuario.aceiteTermoUso ? 'bi-x-circle' : 'bi-check-circle'"></i>
                  {{ usuario.aceiteTermoUso ? 'Revogar Aceite' : 'Aceitar Termo' }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <!-- Footer -->
        <footer class="meu-perfil-footer">
          <div class="d-flex gap-2 w-100 justify-content-end">
            <button type="button" class="btn btn-outline-secondary" @click="cancelar" :disabled="isSaving">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" @click="salvar" :disabled="isSaving || apelidoDisponivel === false">
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-check-lg me-1"></i>
              Salvar
            </button>
          </div>
        </footer>
      </div>
    </div>

    <!-- Modal do Termo de Uso (autossuficiente) -->
    <div v-if="showTermoModal" class="meu-perfil-backdrop" @click.self="showTermoModal = false">
      <div class="meu-perfil-modal termo-modal shadow" role="dialog" aria-modal="true">
        <header class="meu-perfil-header">
          <h5 class="mb-0"><i class="bi bi-file-text me-2"></i>Termo de Uso — Perfil Público</h5>
          <button type="button" class="btn-close btn-close-white" @click="showTermoModal = false" aria-label="Fechar"></button>
        </header>
        <div class="meu-perfil-body">
          <div class="termo-conteudo">
            <h6>1. Objetivo</h6>
            <p>Este termo regulamenta a exibição pública de informações do perfil do usuário no módulo de Sugestões e Melhorias do sistema CTech - ERP Comex, incluindo apelido, foto pública e interações como comentários e @menções.</p>

            <h6>2. Dados Públicos</h6>
            <p>Ao aceitar este termo, o usuário autoriza a exibição dos seguintes dados no feed público de sugestões:</p>
            <ul>
              <li><strong>Apelido público</strong> (definido pelo usuário)</li>
              <li><strong>Foto pública</strong> (quando houver upload)</li>
              <li><strong>Comentários</strong> realizados em sugestões</li>
              <li><strong>@Menções</strong> por outros usuários</li>
            </ul>

            <h6>3. Privacidade e Controle</h6>
            <p>O usuário pode, a qualquer momento, revogar este aceite através da edição do seu perfil. A revogação não remove comentários já publicados, apenas impede nova exibição pública. O usuário pode alterar ou remover seu apelido e foto pública a qualquer momento.</p>

            <h6>4. Responsabilidades</h6>
            <p>O usuário é responsável pelo conteúdo do seu apelido, que não deve conter termos ofensivos, discriminatórios ou ilegais. A administração do sistema reserva-se o direito de remover apelidos inadequados sem aviso prévio. Comentários com @menções são de responsabilidade exclusiva do autor.</p>

            <h6>5. Notificações</h6>
            <p>O aceite de notificações autoriza o envio de e-mails sobre: novos comentários em sugestões criadas pelo usuário, respostas a comentários do usuário, @Menções recebidas e alterações de status em sugestões do usuário.</p>

            <h6>6. Disposições Gerais</h6>
            <p>Este termo segue as diretrizes da LGPD (Lei Geral de Proteção de Dados). Os dados não são compartilhados com terceiros fora do ambiente do CTech - ERP Comex. Dúvidas podem ser encaminhadas à administração do sistema.</p>

            <hr />
            <p class="text-muted small">Versão 1.0 — Setembro 2026</p>
          </div>

          <!-- Checkbox de aceite dentro do modal do termo -->
          <div class="form-check mt-3">
            <input class="form-check-input" type="checkbox" id="aceiteTermoModal" v-model="termoAceiteLocal" />
            <label class="form-check-label" for="aceiteTermoModal">
              <strong>Li e aceito o Termo de Uso</strong>
            </label>
          </div>
        </div>
        <footer class="meu-perfil-footer">
          <div class="d-flex gap-2 w-100 justify-content-end">
            <button type="button" class="btn btn-outline-secondary" @click="showTermoModal = false">Fechar</button>
            <button type="button" class="btn btn-primary" @click="salvarAceiteTermo" :disabled="isSavingTermo">
              <span v-if="isSavingTermo" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-check-lg me-1"></i>
              Guardar Aceite
            </button>
          </div>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script>
import api from '@/services/api'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

export default {
  name: 'MeuPerfilModal',
  props: {
    modelValue: { type: Boolean, default: false },
    usuarioLogado: { type: Object, default: null }
  },
  emits: ['update:modelValue', 'perfil-atualizado'],
  data () {
    return {
      isLoading: false,
      isSaving: false,
      isSavingTermo: false,
      usuario: null,
      form: {
        nomeExibicao: '',
        apelido: '',
        aceiteNotificacoes: false,
        aceiteTermoUso: false
      },
      fotoInternaPreview: null,
      fotoPublicaPreview: null,
      fotoInternaFile: null,
      fotoPublicaFile: null,
      apelidoError: '',
      apelidoDisponivel: null,
      apelidoTimeout: null,
      nomeGuerraError: '',
      usarMesmaFoto: false,
      termoAceiteLocal: false,
      showTermoModal: false,
      // --- Empilhamento (z-index) via registry central ---
      zIndex: BASE_ZINDEX,
      tokenZIndex: null
    }
  },
  computed: {
    isApelidoValido () {
      if (!this.form.apelido) return true
      return /^[a-zA-Z0-9_]+$/.test(this.form.apelido)
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler (val) {
        if (val) {
          const { token, zIndex } = reservarZIndex()
          this.tokenZIndex = token
          this.zIndex = zIndex
          this.carregarDados()
        } else {
          liberarZIndex(this.tokenZIndex)
          this.tokenZIndex = null
          this.limparForm()
        }
      }
    }
  },
  beforeUnmount () {
    liberarZIndex(this.tokenZIndex)
    this.tokenZIndex = null
  },
  methods: {
    async carregarDados () {
      this.isLoading = true
      try {
        const id = this.usuarioLogado?.id
        if (!id) {
          console.error('[MeuPerfilModal] ID do usuário não disponível')
          return
        }
        const response = await api.get(`/Usuarios/${id}`)
        this.usuario = response.data
        this.form.nomeExibicao = this.usuario.nomeExibicao || ''
        this.form.apelido = this.usuario.apelido || ''
        this.form.aceiteNotificacoes = this.usuario.aceiteNotificacoes || false
        this.form.aceiteTermoUso = this.usuario.aceiteTermoUso || false
        this.termoAceiteLocal = this.usuario.aceiteTermoUso || false
        this.fotoInternaPreview = this.usuario.urlFotoInterna || null
        this.fotoPublicaPreview = this.usuario.urlFotoPublica || null
      } catch (error) {
        console.error('[MeuPerfilModal] Erro ao carregar dados:', error)
      } finally {
        this.isLoading = false
      }
    },
    limparForm () {
      this.usuario = null
      this.form = { nomeExibicao: '', apelido: '', aceiteNotificacoes: false, aceiteTermoUso: false }
      this.fotoInternaPreview = null
      this.fotoPublicaPreview = null
      this.fotoInternaFile = null
      this.fotoPublicaFile = null
      this.apelidoError = ''
      this.apelidoDisponivel = null
      this.usarMesmaFoto = false
      this.termoAceiteLocal = false
      this.showTermoModal = false
    },
    validarApelidoInput () {
      // Remove caracteres inválidos enquanto digita
      this.form.apelido = (this.form.apelido || '').replace(/[^a-zA-Z0-9_]/g, '')
    },
    validarNomeGuerra () {
      const nome = this.form.nomeExibicao || ''
      if (!nome.trim()) {
        this.nomeGuerraError = ''
        return
      }
      const palavras = nome.trim().split(/\s+/)
      if (palavras.length > 3) {
        this.nomeGuerraError = 'O nome de guerra deve ter no máximo 3 palavras.'
        return
      }
      if (palavras.some(p => p.length < 2)) {
        this.nomeGuerraError = 'Cada palavra deve ter no mínimo 2 caracteres.'
        return
      }
      this.nomeGuerraError = ''
    },
    verificarApelidoDebounced () {
      if (this.apelidoTimeout) clearTimeout(this.apelidoTimeout)
      const apelido = this.form.apelido
      if (!apelido || apelido.length < 2) {
        this.apelidoError = ''
        this.apelidoDisponivel = null
        return
      }
      if (!this.isApelidoValido) {
        this.apelidoError = ''
        this.apelidoDisponivel = null
        return
      }
      // Se o apelido não mudou, não verifica
      if (apelido === this.usuario?.apelido) {
        this.apelidoError = ''
        this.apelidoDisponivel = null
        return
      }
      this.apelidoTimeout = setTimeout(async () => {
        try {
          const response = await api.get('/Usuarios/apelido-disponivel', { params: { apelido } })
          this.apelidoDisponivel = response.data.disponivel
          this.apelidoError = response.data.disponivel ? '' : 'Apelido já está em uso'
        } catch {
          this.apelidoDisponivel = null
          this.apelidoError = ''
        }
      }, 500)
    },
    onFotoInternaChange (event) {
      const file = event.target.files[0]
      if (!file) return
      this.fotoInternaFile = file
      this.fotoInternaPreview = URL.createObjectURL(file)
      if (this.usarMesmaFoto) {
        this.fotoPublicaFile = file
        this.fotoPublicaPreview = this.fotoInternaPreview
      }
    },
    onFotoPublicaChange (event) {
      const file = event.target.files[0]
      if (!file) return
      this.fotoPublicaFile = file
      this.fotoPublicaPreview = URL.createObjectURL(file)
      if (this.usarMesmaFoto) {
        this.fotoInternaFile = file
        this.fotoInternaPreview = this.fotoPublicaPreview
      }
    },
    aplicarMesmaFoto () {
      // Si se marca y solo una foto está seleccionada, copia a la otra
      if (!this.usarMesmaFoto) return
      if (this.fotoInternaFile && !this.fotoPublicaFile) {
        this.fotoPublicaFile = this.fotoInternaFile
        this.fotoPublicaPreview = this.fotoInternaPreview
      } else if (this.fotoPublicaFile && !this.fotoInternaFile) {
        this.fotoInternaFile = this.fotoPublicaFile
        this.fotoInternaPreview = this.fotoPublicaPreview
      }
    },
    async removerFotoInterna () {
      this.fotoInternaFile = null
      this.fotoInternaPreview = null
      // A remoção será feita no salvar
    },
    async removerFotoPublica () {
      this.fotoPublicaFile = null
      this.fotoPublicaPreview = null
    },
    toggleAceiteTermo () {
      this.form.aceiteTermoUso = !this.form.aceiteTermoUso
    },
    async salvarAceiteTermo () {
      const id = this.usuarioLogado?.id
      if (!id) return
      this.isSavingTermo = true
      try {
        await api.put(`/Usuarios/${id}/aceite-termo`, { aceite: this.termoAceiteLocal })
        // Sincroniza el estado local del modal principal
        this.usuario.aceiteTermoUso = this.termoAceiteLocal
        this.usuario.dataAceiteTermoUso = this.termoAceiteLocal ? new Date().toISOString() : null
        this.form.aceiteTermoUso = this.termoAceiteLocal
        this.showTermoModal = false
        this.$emit('perfil-atualizado')
      } catch (error) {
        console.error('[MeuPerfilModal] Erro al guardar aceite del termo:', error)
        alert('Erro al guardar el aceite del termo. Intente nuevamente.')
      } finally {
        this.isSavingTermo = false
      }
    },
    formatarData (data) {
      if (!data) return ''
      const d = new Date(data)
      return d.toLocaleDateString('pt-BR')
    },
    async salvar () {
      const id = this.usuarioLogado?.id
      if (!id) return

      this.isSaving = true
      try {
        // 1. Upload de fotos se houver
        if (this.fotoInternaFile) {
          const formData = new FormData()
          formData.append('file', this.fotoInternaFile)
          const resp = await api.post(`/Usuarios/${id}/foto-interna`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          this.usuario.urlFotoInterna = resp.data.url
        } else if (this.usuario?.urlFotoInterna && !this.fotoInternaPreview) {
          // Foto removida - não temos endpoint de delete, mas o upload substitui
          // Vamos marcar para remoção enviando null no meu-perfil
        }

        if (this.fotoPublicaFile) {
          const formData = new FormData()
          formData.append('file', this.fotoPublicaFile)
          const resp = await api.post(`/Usuarios/${id}/foto-publica`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          this.usuario.urlFotoPublica = resp.data.url
        }

        // 2. Atualizar perfil
        const payload = {
          nomeExibicao: this.form.nomeExibicao || null,
          apelido: this.form.apelido || null,
          aceiteNotificacoes: this.form.aceiteNotificacoes,
          aceiteTermoUso: this.form.aceiteTermoUso
        }

        await api.put(`/Usuarios/${id}/meu-perfil`, payload)

        this.$emit('perfil-atualizado')
        this.$emit('update:modelValue', false)
      } catch (error) {
        console.error('[MeuPerfilModal] Erro ao salvar:', error)
        alert('Erro ao salvar perfil. Tente novamente.')
      } finally {
        this.isSaving = false
      }
    },
    cancelar () {
      this.$emit('update:modelValue', false)
    }
  }
}
</script>

<style scoped>
.meu-perfil-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
}

.meu-perfil-modal {
  width: 90%;
  max-width: 560px;
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.termo-modal {
  max-width: 640px;
}

.meu-perfil-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: #34495E;
  color: white;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  flex-shrink: 0;
}

.meu-perfil-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex-grow: 1;
}

.meu-perfil-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--bs-border-color-translucent);
  flex-shrink: 0;
  background-color: var(--bs-tertiary-bg);
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.foto-preview {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--bs-border-color);
  flex-shrink: 0;
}

.preview-img-wrapper {
  width: 100%;
  height: 100%;
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-secondary-color);
  font-size: 2rem;
}

.termo-section {
  padding: 1rem;
  background-color: var(--bs-tertiary-bg);
  border-radius: 8px;
  border: 1px solid var(--bs-border-color-translucent);
}

.termo-conteudo {
  font-size: 0.9rem;
  line-height: 1.6;
}

.termo-conteudo h6 {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.termo-conteudo ul {
  padding-left: 1.5rem;
}

.termo-conteudo p {
  margin-bottom: 0.5rem;
}
</style>
